import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

import { apiFetch } from "../../Helper/Api";
import { t } from "../../Helper/i18n";
import Header from "../../components/Header/Header";
import { Modal } from "../../components/UI/Overlays";
import { EmptyState, TableWait, Chip } from "../../components/UI/Feedback";
import { Segmented } from "../../components/UI/Controls";
import "./LibraryManager.scss";

const properties = require("../../properties.json");

export const TAG_TYPES = ["STYLE", "EQUIPMENT", "FOCUS", "BODY_AREA"];

/** "12:05" from seconds. */
export function clock(seconds) {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** The tag ids to send after ticking / un-ticking one. */
export function toggleId(ids, id) {
  return ids.indexOf(id) >= 0 ? ids.filter((x) => x !== id) : [...ids, id];
}

/** The coach's library: every video (or recipe) of their programs, the tags members filter by, and which tags each
 *  one carries. Videos and recipes are uploaded in the programs; this page is where they are labelled. */
export default function LibraryManager() {
  const toast = useRef(null);
  const [kind, setKind] = useState("VIDEO");
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [filterTag, setFilterTag] = useState(null);
  const [result, setResult] = useState(null);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState({ type: "STYLE", name: "" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  const say = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === "error" ? t("common.alert") : t("common.done"), detail });
  const fail = (e) => say("error", (e && e.message) || t("common.failed"));

  const loadTags = () => apiFetch("/library/tags").then(setTags).catch(() => setTags([]));

  const load = useCallback(() => {
    apiFetch("/library", { query: { type: kind, q: q.trim() || undefined, tagIds: filterTag ? String(filterTag) : undefined, page, size: 15, sort: "new" } })
      .then(setResult)
      .catch((e) => {
        setResult({ items: [], total: 0, page: 0, pages: 0, facets: null });
        fail(e);
      });
  }, [kind, q, filterTag, page]);

  useEffect(() => {
    document.title = properties.site_name + " | " + t("gym.library");
    loadTags();
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(load, 250);
    return () => clearTimeout(timer.current);
  }, [load]);

  const addTag = () => {
    if (!newTag.name.trim()) return;
    apiFetch("/library/tags", { method: "POST", body: { type: newTag.type, name: newTag.name.trim() } })
      .then(() => {
        setNewTag({ ...newTag, name: "" });
        return loadTags();
      })
      .catch(fail);
  };

  const removeTag = (tag) => {
    if (!window.confirm(t("gym.library.deleteTag", { name: tag.name }))) return;
    apiFetch(`/library/tags/${tag.id}`, { method: "DELETE" })
      .then(() => {
        if (filterTag === tag.id) setFilterTag(null);
        loadTags();
        load();
      })
      .catch(fail);
  };

  const saveItemTags = () => {
    setSaving(true);
    apiFetch(`/library/items/${editing.item.itemId}/tags`, { method: "PUT", body: { tagIds: editing.ids } })
      .then(() => {
        setEditing(null);
        say("success", t("gym.library.saved"));
        load();
      })
      .catch(fail)
      .finally(() => setSaving(false));
  };

  const items = result ? result.items : null;

  return (
    <>
      <Header pageName={t("gym.library")} />
      <Toast ref={toast} />

      <Modal
        visible={!!editing}
        onHide={() => setEditing(null)}
        title={editing ? t("gym.library.tagsOf", { title: editing.item.title }) : ""}
        footer={<button type="button" className="ui-btn" disabled={saving} onClick={saveItemTags}><i className="pi pi-check"></i> {t("common.save")}</button>}
      >
        {editing ? (
          <div className="ui-modal-form lm-tagpicker">
            {tags.length === 0 ? <p>{t("gym.library.noTags")}</p> : null}
            {TAG_TYPES.map((type) => {
              const list = tags.filter((x) => x.type === type);
              if (!list.length) return null;
              return (
                <div key={type}>
                  <h4>{t("gym.library.type." + type)}</h4>
                  <div className="lm-chips">
                    {list.map((x) => (
                      <button type="button" key={x.id} className={`lm-chip ${editing.ids.indexOf(x.id) >= 0 ? "on" : ""}`} onClick={() => setEditing({ ...editing, ids: toggleId(editing.ids, x.id) })}>
                        {x.name}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </Modal>

      <div className="content">
        <div className="container">
          <div className="card">
            <Card title={t("gym.library.tags")}>
              <p className="lm-hint">{t("gym.library.tagsHint")}</p>
              {TAG_TYPES.map((type) => (
                <div className="lm-group" key={type}>
                  <b>{t("gym.library.type." + type)}</b>
                  <div className="lm-chips">
                    {tags.filter((x) => x.type === type).map((x) => (
                      <span className="lm-tag" key={x.id}>
                        {x.name}
                        <button type="button" aria-label={t("gym.library.remove")} onClick={() => removeTag(x)}><i className="pi pi-times" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              <div className="lm-add">
                <Dropdown options={TAG_TYPES.map((x) => ({ label: t("gym.library.type." + x), value: x }))} value={newTag.type} onChange={(e) => setNewTag({ ...newTag, type: e.value })} />
                <InputText value={newTag.name} maxLength={60} placeholder={t("gym.library.tagName")} onChange={(e) => setNewTag({ ...newTag, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addTag()} />
                <Button icon="pi pi-plus" label={t("gym.library.addTag")} onClick={addTag} />
              </div>
            </Card>
          </div>

          <div className="card" style={{ marginTop: 16 }}>
            <Card>
              <div className="lm-toolbar">
                <Segmented
                  value={kind}
                  onChange={(k) => {
                    setKind(k);
                    setPage(0);
                  }}
                  items={[{ key: "VIDEO", label: t("gym.library.videos") }, { key: "ARTICLE", label: t("gym.library.recipes") }]}
                />
                <InputText value={q} placeholder={t("gym.library.search")} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
                <Dropdown showClear options={tags.map((x) => ({ label: x.name, value: x.id }))} value={filterTag} placeholder={t("gym.library.byTag")} onChange={(e) => { setFilterTag(e.value || null); setPage(0); }} />
              </div>
              <DataTable
                value={items || []}
                className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                emptyMessage={items === null ? <TableWait /> : <EmptyState icon="pi pi-video" title={t("gym.library.emptyTitle")} hint={t("gym.library.emptyText")} />}
              >
                <Column field="title" header={t("gym.library.title")} />
                <Column field="courseTitle" header={t("gym.library.program")} />
                <Column header={t("gym.library.duration")} body={(r) => clock(r.durationSeconds)} />
                <Column header={t("gym.library.tags")} body={(r) => (r.tags.length ? r.tags.map((x) => <Chip key={x.id} kind="wait">{x.name}</Chip>) : <span className="lm-none">—</span>)} />
                <Column header="" body={(r) => <Button icon="pi pi-tags" className="p-button-rounded p-button-secondary" title={t("gym.library.editTags")} onClick={() => setEditing({ item: r, ids: r.tags.map((x) => x.id) })} />} />
              </DataTable>
              {result && result.pages > 1 ? (
                <div className="lm-pager">
                  <Button icon="pi pi-chevron-right" className="p-button-text" disabled={result.page <= 0} onClick={() => setPage(result.page - 1)} />
                  <span>{result.page + 1} / {result.pages}</span>
                  <Button icon="pi pi-chevron-left" className="p-button-text" disabled={result.page + 1 >= result.pages} onClick={() => setPage(result.page + 1)} />
                </div>
              ) : null}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
